// src/utils/asistenteIA.js
// Asistente IA — Groq + Llama 4 Scout (500K tokens/día)
import { fetchAPI } from "./api";
import { aiTools } from "../ia/aiSchema";
import { ejecutarFuncionAI } from "../ia/aiParser";
import { filtrarHerramientas, avisoModulosBloqueados } from "../ia/permisosIA";

const API_URL = process.env.REACT_APP_API_URL || "https://backend-emmita.onrender.com";
const MAX_TOOL_ROUNDS = 5;
const MAX_HISTORIAL = 6;

export const NOMBRE_ASISTENTE = "Renty";

const ANIO = new Date().getFullYear();

// ═══════════════════════════════════════════════════════════
// 📅 Fechas resueltas EN CÓDIGO.
//
// Llama no sabe hacer aritmética de calendario. Diciéndole solo "hoy es lunes
// 27 de julio" y preguntándole por "el próximo sábado", contestó 2026-08-03
// (que es lunes). Se las damos ya calculadas para que no tenga que pensar.
//
// ⚠️ Nada de toISOString(): en Colombia (UTC-5) se corre un día.
// ═══════════════════════════════════════════════════════════
function fechasUtiles() {
  const hoy = new Date();
  const iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const masDias = (n) => { const d = new Date(hoy); d.setDate(d.getDate() + n); return d; };

  // Siguiente ocurrencia ESTRICTAMENTE después de hoy
  const siguienteDia = (objetivo) => masDias(((objetivo - hoy.getDay() + 7) % 7) || 7);

  const iniMes = (off) => new Date(hoy.getFullYear(), hoy.getMonth() + off, 1);
  const finMes = (off) => new Date(hoy.getFullYear(), hoy.getMonth() + off + 1, 0);

  const dias = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];

  const semana = dias
    .map((n, i) => `  · este ${n} / el próximo ${n} = ${iso(siguienteDia(i))}`)
    .join("\n");

  return `FECHAS YA CALCULADAS (cópialas TAL CUAL, jamás calcules una fecha tú mismo):
- hoy = ${iso(hoy)} (${dias[hoy.getDay()]})
- mañana = ${iso(masDias(1))}
- pasado mañana = ${iso(masDias(2))}
- de aquí a una semana = ${iso(masDias(7))}
${semana}
- mes actual: desde ${iso(iniMes(0))} hasta ${iso(finMes(0))}
- mes pasado: desde ${iso(iniMes(-1))} hasta ${iso(finMes(-1))}
- año actual: desde ${ANIO}-01-01 hasta ${ANIO}-12-31
- año pasado: desde ${ANIO - 1}-01-01 hasta ${ANIO - 1}-12-31`;
}

const SYSTEM_PROMPT = `Eres ${NOMBRE_ASISTENTE}, el asistente de SwAlquiler — una plataforma colombiana de gestión de alquiler de mobiliario y eventos. Año: ${ANIO}.

PERSONALIDAD:
- Hablas en español colombiano natural, como un compañero de trabajo amable y eficiente. Nada de "Estimado usuario" ni formalidad exagerada.
- Eres directo pero cálido. Dices "listo", "dale", "claro", "va" cuando corresponde.
- Si no encuentras algo, ofreces alternativas útiles ("No encontré ese artículo exacto, pero tengo estos que se parecen...").
- Cuando algo sale bien dices cosas como "Listo, quedó agendado" o "Ya te lo busqué", no "He procedido a realizar la consulta exitosamente".
- Máximo 200 palabras por respuesta. Conciso y práctico.
- Usas emojis con moderación, solo para dar contexto visual (📦 para productos, 💰 para dinero, 📅 para fechas, 🤝 para citas).

REGLAS TÉCNICAS:
- USA las herramientas para responder. NUNCA inventes datos.
- Precios siempre en pesos colombianos ($xxx.xxx).
- NUNCA muestres _id, _tipo, _acciones, JSON ni código al usuario.
- Usa numeración en listas largas.
- Antes de crear un cliente nuevo, pregunta: "¿Me das los datos del cliente? Nombre completo, teléfono, y si tienes: cédula, email o dirección." Espera la respuesta y crea el cliente con lo que te den. Después continúa con la cita.
- Fechas futuras son del año actual (${ANIO}) salvo que digan otro año.
- Si el usuario menciona ARTÍCULO + CLIENTE juntos → usa trazabilidad_precio. ultimo_cliente_articulo es SOLO sin cliente específico.

CITAS:
- Puedes agendar, consultar y cambiar estado de citas con clientes.
- Para agendar necesitas: cliente + fecha + motivo. Si no te dan el motivo, pregúntalo antes de crear la cita. Hora es opcional (default 9:00 AM).
- Si te piden "confirmar cita" usa cambiar_estado_cita con nuevo_estado "confirmada".
- Si preguntan por notificaciones, usa consultar_notificaciones.
- IMPORTANTE: Cuando crear_cita devuelva una lista de clientes similares o pregunte si crear un cliente nuevo, muestra ese mensaje TAL CUAL al usuario y ESPERA su respuesta. NO intentes resolver la ambigüedad tú solo llamando más herramientas. El usuario debe elegir.
- IMPORTANTE: Cuando CUALQUIER herramienta devuelva opciones o preguntas de confirmación, reenvía el mensaje al usuario. NUNCA llames crear_cliente automáticamente sin que el usuario lo pida explícitamente.

COMPARATIVAS:
- Si preguntan "cómo va este mes vs el pasado" o similares, usa comparativa_financiera.
- Da un análisis breve y útil de la tendencia, no solo los números.

CRM:
- Si preguntan por un cliente específico ("cuéntame de X", "cómo va X", "ficha de X", "historial de X", "qué le gusta a X", "artículo más alquilado de X") → usa ficha_cliente. Da un resumen ejecutivo: segmento, recaudo, productos favoritos, estado de pagos.
- Si preguntan por alertas, pendientes del negocio, qué falta por cobrar, clientes inactivos → usa consultar_alertas_crm.
- Si preguntan por clientes de un segmento ("clientes VIP", "quiénes están en riesgo", "clientes dormidos") → usa listar_clientes_segmento.
- Si preguntan cuántas unidades de un artículo se alquilaron o están reservadas ("cuántas sillas alquilé en mayo", "cuántas mesas tengo para el 20 de junio") → usa consultar_uso_articulo.
- En pedidos y cotizaciones incluyes tipo_evento y lugar_evento cuando estén disponibles.

REGLA ANTI-INVENCIÓN (CRÍTICA):
- NUNCA respondas sobre datos de clientes, pedidos, artículos, finanzas o inventario sin usar una herramienta primero.
- Si no tienes una herramienta para la consulta, di honestamente que no puedes responderla aún.
- Si el usuario solo agradece, se despide o hace un comentario social ("gracias", "muy bien", "ok"), responde en UNA frase corta y NO llames ninguna herramienta.
- NUNCA llames la misma herramienta con los mismos argumentos dos veces. Si ya la llamaste, usa el resultado que tienes.
- PROHIBIDO SUSTITUIR: si no tienes la herramienta exacta, NO uses otra parecida presentando su resultado como si fuera la respuesta. Ejemplo real de lo que NUNCA debes hacer: te preguntan por los INGRESOS del mes pasado, no tienes la herramienta financiera, y respondes con el CONTEO total de pedidos. Eso es un error grave. Di que no puedes y punto.
- Respeta el alcance de cada resultado. Si una herramienta te devuelve un total histórico, NO lo presentes como si fuera de un mes o de un período.
- SIEMPRE usa ficha_cliente cuando pregunten sobre un cliente específico (su historial, productos favoritos, deudas, segmento).
- SIEMPRE usa consultar_uso_articulo cuando pregunten cantidades de un artículo en un período.
- Si ya consultaste una herramienta, basa tu respuesta ÚNICAMENTE en los datos devueltos. No agregues datos que no estén en la respuesta.

LÓGICA DE FECHAS:
- NUNCA calcules una fecha tú mismo. Al final de estas instrucciones tienes un bloque de FECHAS YA CALCULADAS: úsalo siempre.
- Si te piden un día de la semana que no esté en ese bloque, PREGUNTA la fecha exacta en vez de adivinar.
- Cuando respondas sobre una fecha relativa, di también la fecha concreta ("el sábado 1 de agosto") para que el usuario pueda verificarte.
- Si dicen "el mes pasado", "en mayo", "el año pasado" → fechas del pasado, modo histórico.
- Si dicen "para el 20 de junio", "la próxima semana", "mañana" → fechas futuras, modo reservado.
- Si solo dicen un mes sin año y ese mes ya pasó este año, asume este año.
- Fechas futuras sin especificar son del año ${ANIO}.`;

function extraerAcciones(toolResults) {
  const acciones = [], ids = new Set();
  for (const r of toolResults) {
    try {
      const d = typeof r === "string" ? JSON.parse(r) : r;
      const procesar = (items) => {
        for (const it of items || []) {
          if (it._id && it._tipo && !ids.has(it._id)) {
            ids.add(it._id);
            acciones.push({ id: it._id, tipo: it._tipo, numero: it.numero || "—" });
          }
        }
      };
      if (Array.isArray(d)) procesar(d);
      else if (d?._acciones) procesar(d._acciones);
      else if (d?.pedidos) { procesar(d.pedidos); procesar(d.cotizaciones || []); }
    } catch {}
  }
  return acciones;
}

async function llamarAPI(messages, tools) {
  return fetchAPI(`${API_URL}/api/ia/chat`, {
    method: "POST",
    body: JSON.stringify({ messages, tools, tool_choice: "auto" }),
  });
}

/**
 * Mensajes puramente sociales: agradecimientos, despedidas, "ok", "muy bien".
 *
 * Antes se los mandábamos a Llama con las 27 herramientas encima. El modelo,
 * presionado por las reglas de "siempre usa herramientas", volvía a llamar la
 * última que había usado, gastaba las 5 rondas, disparaba el límite de Groq y
 * terminaba en "No pude completar la consulta". Se responden aquí mismo:
 * instantáneo, sin red y sin gastar cupo.
 */
const SOCIALES = [
  { re: /^(muchas |mil |muy amable,? )?gracias[\s.!]*$/i, r: ["Con gusto 🤝", "Para eso estoy 👍", "Cuando necesites."] },
  { re: /^(muy bien|perfecto|excelente|genial|listo|ok|okay|vale|dale|entendido|bueno)[\s.!]*$/i, r: ["👍 ¿Algo más?", "Listo, aquí sigo.", "Perfecto."] },
  { re: /^(chao|adios|adiós|hasta luego|nos vemos|bye)[\s.!]*$/i, r: ["¡Hasta luego! 👋", "Nos vemos, cualquier cosa me escribes."] },
];

function respuestaSocial(texto) {
  const t = String(texto || "").trim();
  // Frases largas o con pregunta pueden traer algo real adentro: esas sí van a Groq.
  if (!t || t.length > 30 || t.includes("?")) return null;
  for (const s of SOCIALES) {
    if (s.re.test(t)) return s.r[Math.floor(Math.random() * s.r.length)];
  }
  return null;
}

export async function consultarIA(mensajeOriginal, historial = [], ctx = {}) {
  // 🤝 Cortesías: se resuelven aquí, sin ir a Groq ni descontar del cupo
  const social = respuestaSocial(mensajeOriginal);
  if (social) return { texto: social, acciones: [], sinCosto: true };
  try {
    // 🔒 Bloque 7D — solo viajan las herramientas que este usuario puede usar
    const herramientas = filtrarHerramientas(aiTools, ctx);

    // 📅 Fechas frescas en CADA consulta: si alguien deja la pestaña abierta
    //    pasada la medianoche, no se queda pensando que hoy es ayer.
    const contexto = `${SYSTEM_PROMPT}\n\n${fechasUtiles()}${avisoModulosBloqueados(ctx)}`;

    const messages = [{ role: "system", content: contexto }];
    for (const msg of historial.slice(-MAX_HISTORIAL)) {
      if (msg.tipo === "user") messages.push({ role: "user", content: msg.texto });
      else if (msg.tipo === "ia" && msg.texto) messages.push({ role: "assistant", content: msg.texto });
    }
    messages.push({ role: "user", content: mensajeOriginal });

    let respuestaFinal = null, rondas = 0;
    const toolResults = [];
    // 🔁 Firmas de herramientas ya ejecutadas en esta consulta.
    //    Llama se queda pegado repitiendo la misma llamada con los mismos
    //    argumentos hasta agotar las rondas (lo vimos 6 veces seguidas con
    //    pagos_pendientes). Si ya tenemos el dato, se lo recordamos.
    const yaEjecutadas = new Set();

    while (!respuestaFinal && rondas < MAX_TOOL_ROUNDS) {
      rondas++;

      let data;
      try {
        data = await llamarAPI(messages, herramientas);
      } catch (err) {
        if (err.message?.includes("502") || err.message?.includes("503")) {
          console.log("🔄 Reintentando tras 502...");
          await new Promise((r) => setTimeout(r, 2500));
          try {
            data = await llamarAPI(messages, herramientas);
          } catch (err2) {
            console.log("🔄 Segundo reintento...");
            await new Promise((r) => setTimeout(r, 3000));
            data = await llamarAPI(messages, herramientas);
          }
        } else throw err;
      }

      const msg = data.choices?.[0]?.message;
      if (!msg) return { texto: "Ups, no obtuve respuesta. ¿Puedes intentar de nuevo?", acciones: [] };

      if (msg.tool_calls?.length > 0) {
        messages.push({ role: "assistant", content: msg.content || null, tool_calls: msg.tool_calls });
        for (const tc of msg.tool_calls) {
          const nombre = tc.function?.name;
          const firma = `${nombre}:${tc.function?.arguments || ""}`;
          let resultado;

          if (yaEjecutadas.has(firma)) {
            console.warn(`🔁 Repetida, no se re-ejecuta: ${nombre}`);
            resultado = JSON.stringify({
              nota: "Ya ejecutaste esta misma consulta con estos mismos argumentos y su resultado está más arriba. NO la repitas. Responde ahora en texto con lo que tienes, o dile al usuario claramente que no encontraste lo que buscaba.",
            });
          } else {
            console.log(`🔧 Ejecutando: ${nombre}`, tc.function?.arguments);
            yaEjecutadas.add(firma);
            try { resultado = await ejecutarFuncionAI(nombre, tc.function?.arguments, ctx); }
            catch { resultado = JSON.stringify({ error: true, mensaje: "Error" }); }
          }
          toolResults.push(resultado);
          messages.push({ role: "tool", tool_call_id: tc.id, content: resultado });

          // Si la función devuelve opciones o pide confirmación, parar y mostrar al usuario
          if (typeof resultado === "string" && (resultado.includes("se parecen") || resultado.includes("¿Quieres que lo registre") || resultado.includes("¿Es alguno de") || resultado.includes("¿Con cuál") || resultado.includes("necesito más datos") || resultado.includes("necesito sus datos") || resultado.includes("¿Me das"))) {
            respuestaFinal = resultado;
            break;
          }
        }
        if (respuestaFinal) break;
        continue;
      }
      respuestaFinal = msg.content;
    }

    // Se acabaron las rondas sin respuesta en texto. En vez de rendirnos,
    // pedimos una última vez SIN herramientas: así el modelo queda obligado a
    // redactar con lo que ya recopiló.
    if (!respuestaFinal) {
      try {
        const cierre = await llamarAPI(
          [...messages, { role: "user", content: "Responde ahora en texto con lo que ya tienes. No llames más herramientas. Si no encontraste lo que se pedía, dilo claramente." }],
          undefined
        );
        respuestaFinal = cierre?.choices?.[0]?.message?.content || null;
      } catch (e) {
        console.warn("No se pudo forzar la respuesta final:", e.message);
      }
    }

    if (!respuestaFinal) return { texto: "No pude completar la consulta. Intenta reformular la pregunta.", acciones: [] };
    return { texto: respuestaFinal.trim(), acciones: extraerAcciones(toolResults) };

  } catch (error) {
    console.error("❌ Error en consultarIA:", error.message);
    // 🆕 error: true → la consulta nunca llegó a Groq (502 de Render, red caída...).
    //    Quien nos llama usa esta bandera para NO descontar del cupo diario.
    if (String(error.message || "").includes("400")) {
      return { texto: "Se me enredó la consulta. ¿Puedes escribirla de otra forma? A veces me pasa con nombres poco comunes o mal escritos.", acciones: [], error: true };
    }
    if (String(error.message || "").includes("429")) {
      return { texto: "Estoy recibiendo muchas consultas seguidas. Espera unos segundos y vuelve a intentarlo. ⏳", acciones: [], error: true };
    }
    const fb = fallbackLocal(mensajeOriginal);
    if (fb) return { texto: fb, acciones: [], error: true };
    return { texto: "Parece que hay un problema de conexión. Verifica tu internet e intenta de nuevo.", acciones: [], error: true };
  }
}

function fallbackLocal(m) {
  m = m.toLowerCase();
  if (m.includes("hola") || m.includes("buenas") || m.includes("hey")) return `👋 ¡Hola! Soy ${NOMBRE_ASISTENTE}, tu mano derecha en SwAlquiler. ¿En qué te ayudo?`;
  if (m.includes("qué puedes") || m.includes("ayuda") || m.includes("que sabes")) return `Puedo ayudarte con:\n\n📦 Inventario y disponibilidad\n👥 Buscar clientes, crearlos y ver su ficha completa\n📋 Pedidos, cotizaciones y agenda\n🤝 Agendar y consultar citas\n💰 Finanzas y comparativas\n🔔 Notificaciones y alertas del negocio\n📊 Rankings, segmentación y análisis CRM\n\n¡Pregúntame lo que necesites!`;
  if (m.includes("gracias") || m.includes("thanks")) return `¡Con gusto! Aquí estoy si necesitas algo más. 🤝`;
  return null;
}